import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if user is authenticated on mount
  useEffect(() => {
    let isMounted = true;
    
    const checkAuth = async () => {
      try {
        // First check localStorage
        const storedUser = localStorage.getItem('user');
        if (storedUser && isMounted) {
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);
        }
        
        // Then try to verify with server (but don't clear localStorage if it fails)
        try {
          const response = await fetch(`${API_URL}/api/auth/me`, {
            credentials: 'include'
          });
          
          if (response.ok && isMounted) {
            const userData = await response.json();
            setUser(userData);
            localStorage.setItem('user', JSON.stringify(userData));
          }
          // If server returns 401, keep the localStorage user (cookie might not work in dev)
        } catch (fetchError) {
          // Server auth check failed - this is expected in development environments
          // Log only in development for debugging
          if (process.env.NODE_ENV === 'development') {
            console.warn('Server auth check failed, using localStorage fallback');
          }
        }
      } catch (parseError) {
        // localStorage parse error - clear invalid data
        localStorage.removeItem('user');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    checkAuth();
    
    return () => {
      isMounted = false;
    };
  }, []);

  const login = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      // Logout API failed - continue with local cleanup anyway
      // This is intentional: even if server logout fails, we clear local state
      if (process.env.NODE_ENV === 'development') {
        console.warn('Logout API error:', error.message);
      }
    } finally {
      setUser(null);
      localStorage.removeItem('user');
      window.location.href = '/';
    }
  }, []);

  const setUserData = useCallback((userData) => {
    setUser(userData);
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    user,
    loading,
    login,
    logout,
    setUserData
  }), [user, loading, login, logout, setUserData]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
